import React from "react";
import { Offcanvas, OffcanvasBody, OffcanvasHeader } from "reactstrap";
import moment from "moment";
import config from "../../config";
import { sanitizeHtml } from "../../helpers/sanitizeHtml";

interface NotificationDetailOffcanvasProps {
    isOpen: boolean;
    notification: any | null;
    onClose: () => void;
}

const getTitle = (notification: any): string => {
    if (!notification || typeof notification !== "object") {
        return "Chi tiết thông báo";
    }

    const candidates = [notification.title, notification.subject, notification.name];
    for (const candidate of candidates) {
        if (typeof candidate === "string" && candidate.trim().length > 0) {
            return candidate;
        }
    }

    return "Chi tiết thông báo";
};

const getMessage = (notification: any): string => {
    if (!notification || typeof notification !== "object") {
        return "";
    }

    const candidates = [
        notification.message,
        notification.body,
        notification.content,
        notification.description,
        notification.detail,
        notification.text,
    ];

    for (const candidate of candidates) {
        if (typeof candidate === "string" && candidate.trim().length > 0) {
            return candidate;
        }
    }

    return "";
};

const resolveAssetBaseUrl = (): string => {
    const configuredApiUrl = (process.env.REACT_APP_API_URL ?? config.api.API_URL ?? "").trim();
    if (configuredApiUrl) {
        try {
            const base = new URL(configuredApiUrl, window.location.origin);
            return `${base.origin}/`;
        } catch (_error) {
            // fallback below
        }
    }

    return `${window.location.origin}/`;
};

const normalizeNotificationHtml = (html: string): string => {
    if (typeof window === "undefined" || !html) {
        return html;
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    const baseUrl = resolveAssetBaseUrl();

    doc.querySelectorAll("img[src]").forEach((image) => {
        const srcValue = image.getAttribute("src");
        if (!srcValue || /^(data:|https?:\/\/|\/\/)/i.test(srcValue)) {
            return;
        }

        try {
            image.setAttribute("src", new URL(srcValue, baseUrl).toString());
        } catch (_error) {
            // keep original src
        }
    });

    return sanitizeHtml(doc.body.innerHTML);
};

const getTimestamp = (notification: any): string | null => {
    if (!notification || typeof notification !== "object") {
        return null;
    }

    const candidates = [
        notification.createdAt,
        notification.created_at,
        notification.timestamp,
        notification.time,
        notification.sentAt,
        notification.sent_at,
        notification.date,
        notification.updatedAt,
        notification.updated_at,
    ];

    for (const candidate of candidates) {
        if (candidate !== undefined && candidate !== null && String(candidate).trim().length > 0) {
            const date = moment(candidate);
            if (date.isValid()) {
                return date.format("DD/MM/YYYY HH:mm");
            }

            return String(candidate);
        }
    }

    return null;
};

const NotificationDetailOffcanvas: React.FC<NotificationDetailOffcanvasProps> = ({
    isOpen,
    notification,
    onClose,
}) => {
    const title = getTitle(notification);
    const message = getMessage(notification);
    const normalizedMessage = normalizeNotificationHtml(message);
    const timestamp = getTimestamp(notification);

    return (
        <Offcanvas
            fade
            isOpen={isOpen}
            direction="bottom"
            toggle={onClose}
            className="min-vh-100"
        >
            <OffcanvasHeader toggle={onClose} className="border-bottom">
                {title}
            </OffcanvasHeader>
            <OffcanvasBody>
                {timestamp && <p className="text-muted small mb-3">{timestamp}</p>}

                {normalizedMessage ? (
                    <div dangerouslySetInnerHTML={{ __html: normalizedMessage }} />
                ) : (
                    <p className="text-muted mb-0">Không có nội dung thông báo.</p>
                )}
            </OffcanvasBody>
        </Offcanvas>
    );
};

export default NotificationDetailOffcanvas;
